import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { $isListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import { $getSelectionStyleValueForProperty } from '@lexical/selection';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  EditorState,
  ElementFormatType,
  TextFormatType,
} from 'lexical';
import { useDebounceCallback } from 'usehooks-ts';

import AlignButtons from './components/AlignButtons';
import BlockTypeDropdown from './components/BlockTypeDropdown';
import HistoryButtons from './components/HistoryButtons';
import TextFormatOptions from './components/TextFormatOptions';
import { BlockType, ToolbarContext, ToolbarContextType } from './ToolbarContext';
import { EditorType } from './types';

const TEXT_FORMAT_TYPES = [
  'bold',
  'underline',
  'strikethrough',
  'italic',
  'highlight',
  'code',
  'subscript',
  'superscript',
  'lowercase',
  'uppercase',
  'capitalize',
] as const;

interface ToolbarPluginProps {
  editorType: EditorType;
  children?: ReactNode;
}

export function ToolbarPlugin({ children, editorType }: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [activeTextFormats, setActiveTextFormats] = useState<Set<TextFormatType>>(new Set());
  const [elementFormat, setElementFormat] = useState<ElementFormatType>('left');
  const [fontColor, setFontColor] = useState<string>('#000000');
  const [bgColor, setBgColor] = useState<string>('#FFFFFF');
  const [blockType, setBlockType] = useState<BlockType>('paragraph');

  // function to update the toolbar state from the current editor state
  const updateToolbarState = useCallback((editorState: EditorState) => {
    editorState.read(() => {
      // get the selection
      const selection = $getSelection();

      // set to store active text format types
      const formats = new Set<TextFormatType>();

      if ($isRangeSelection(selection)) {
        // add each active format type to the set
        TEXT_FORMAT_TYPES.forEach((type) => {
          if (selection.hasFormat(type)) formats.add(type);
        });

        // update the active text formats
        setActiveTextFormats(formats);

        // get the top-level block node
        const anchorNode = selection.anchor.getNode();
        let element =
          anchorNode.getKey() === 'root'
            ? anchorNode
            : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

        if (element === null) {
          element = anchorNode.getTopLevelElementOrThrow();
        }

        // set alignment
        if ($isElementNode(element)) {
          const alignment = element.getFormatType() || 'left';
          setElementFormat(alignment);
        }

        // check if element is a list node
        if ($isListNode(element)) {
          const listType = element.getListType();
          setBlockType(listType);
        } else if ($isHeadingNode(element)) {
          setBlockType(element.getTag());
        } else {
          // default to paragraph
          setBlockType('paragraph');
        }

        // get font color
        setFontColor($getSelectionStyleValueForProperty(selection, 'color', '#000000'));

        // get background color
        setBgColor($getSelectionStyleValueForProperty(selection, 'background-color', '#FFFFFF'));
      }
    });
  }, []);

  // debounce updating toolbar state
  const updateToolbarStateDebounced = useDebounceCallback(updateToolbarState, 50);

  useEffect(() => {
    const canUndoCommand = editor.registerCommand<boolean>(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );

    const canRedoCommand = editor.registerCommand<boolean>(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );

    const onEditorChange = editor.registerUpdateListener(({ editorState }) => {
      updateToolbarStateDebounced(editorState);
    });

    // register commands
    return mergeRegister(canUndoCommand, canRedoCommand, onEditorChange);
  }, [updateToolbarStateDebounced, editor]);

  // toolbar context value
  const value = useMemo<ToolbarContextType>(
    () => ({
      editorType,
      canUndo,
      canRedo,
      activeTextFormats,
      elementFormat,
      fontColor,
      bgColor,
      blockType,
    }),
    [editorType, canUndo, canRedo, activeTextFormats, elementFormat, fontColor, bgColor, blockType],
  );

  return (
    <ToolbarContext.Provider value={value}>
      <Toolbar variant="dense" disableGutters>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexGrow={1}
          sx={{
            overflow: 'scroll',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <HistoryButtons />
          {editorType === 'rich' && (
            <>
              <Divider orientation="vertical" variant="middle" flexItem />
              <BlockTypeDropdown />
              <Divider orientation="vertical" variant="middle" flexItem />
              <AlignButtons />
              <Divider orientation="vertical" variant="middle" flexItem />
              <TextFormatOptions menuOnlyActions={['lowercase', 'uppercase', 'capitalize', 'highlight', 'code']} />
            </>
          )}

          {/* show any custom buttons */}
          {children}
        </Stack>
      </Toolbar>
    </ToolbarContext.Provider>
  );
}

export default ToolbarPlugin;
